<script setup lang="ts">
import { computed, ref } from 'vue';
import {
  carrierDisplay,
  carrierLogoUrl,
  deliveryTypeName,
  enabledOptionNames,
  packageTypeName,
} from '../lib/shipmentDisplay';

/**
 * The gate in front of a shipment. The backend has already suspended the turn;
 * nothing is created until "Aanmaken" is pressed, so this card renders the exact
 * arguments the model proposed rather than a paraphrase of them.
 */

const props = defineProps<{
  name: string;
  input: unknown;
  state: 'pending' | 'approved' | 'declined';
  busy: boolean;
}>();

const emit = defineEmits<{
  approve: [];
  decline: [reason?: string];
}>();

const showReason = ref(false);
const reason = ref('');
const showRaw = ref(false);

const title = computed(() =>
  props.name === 'create_return_shipment' ? 'Retourlabel aanmaken?' : 'Verzending aanmaken?',
);

type Recipient = {
  person?: string;
  company?: string;
  street?: string;
  number?: string;
  number_suffix?: string;
  postal_code?: string;
  city?: string;
  cc?: string;
};

const record = computed<Record<string, unknown>>(() =>
  typeof props.input === 'object' && props.input !== null
    ? (props.input as Record<string, unknown>)
    : {},
);

const recipient = computed<Recipient>(() => {
  const value = record.value['recipient'];
  return typeof value === 'object' && value !== null ? (value as Recipient) : {};
});

const addressLines = computed(() => {
  const person = recipient.value;
  const houseNumber = [person.number, person.number_suffix].filter(Boolean).join('');
  const lines = [
    [person.person, person.company].filter(Boolean).join(' — '),
    [person.street, houseNumber].filter(Boolean).join(' '),
    [person.postal_code, person.city].filter(Boolean).join(' '),
    person.cc,
  ];
  return lines.filter((line) => line && line.length > 0);
});

/**
 * The person approves what this card says, so numeric enums are translated to names —
 * "Vervoerder 1" reads as a code they cannot check, "PostNL" as a fact they can. An
 * unknown id falls back to showing the number, never to a guessed name.
 */
const carrier = computed(() => carrierDisplay(record.value['carrier']));
const carrierLogo = computed(() => (carrier.value ? carrierLogoUrl(carrier.value) : null));

const summaryRows = computed(() => {
  const options = record.value['options'];
  const optionRecord =
    typeof options === 'object' && options !== null ? (options as Record<string, unknown>) : {};

  const rows: Array<{ label: string; value: string }> = [];

  if (record.value['carrier'] !== undefined && !carrier.value) {
    rows.push({ label: 'Vervoerder', value: `Vervoerder ${String(record.value['carrier'])}` });
  }
  if (optionRecord['package_type'] !== undefined) {
    rows.push({ label: 'Pakkettype', value: packageTypeName(optionRecord['package_type']) });
  }
  if (optionRecord['delivery_type'] !== undefined) {
    rows.push({ label: 'Bezorging', value: deliveryTypeName(optionRecord['delivery_type']) });
  }
  const extras = enabledOptionNames(optionRecord);
  if (extras.length > 0) {
    rows.push({ label: 'Extra', value: extras.join(', ') });
  }
  if (record.value['reference_identifier'] !== undefined) {
    rows.push({ label: 'Referentie', value: String(record.value['reference_identifier']) });
  }
  if (record.value['parent_shipment_id'] !== undefined) {
    rows.push({ label: 'Oorspronkelijke zending', value: String(record.value['parent_shipment_id']) });
  }

  return rows;
});

function submitDecline(): void {
  emit('decline', reason.value.trim() || undefined);
  showReason.value = false;
  reason.value = '';
}
</script>

<template>
  <div class="confirm" :class="`confirm--${state}`">
    <div class="confirm__header">
      <span class="confirm__icon" aria-hidden="true">!</span>
      <h3>{{ title }}</h3>
    </div>

    <p v-if="state === 'pending'" class="confirm__lead">
      Dit maakt een echte zending aan en wordt gefactureerd. Controleer de gegevens.
    </p>

    <div v-if="carrier" class="confirm__carrier">
      <img
        :src="carrierLogo ?? undefined"
        :alt="carrier.name"
        class="confirm__carrierLogo"
        width="28"
        height="28"
      />
      <span>{{ carrier.name }}</span>
    </div>

    <dl class="confirm__body">
      <template v-if="addressLines.length > 0">
        <dt>Ontvanger</dt>
        <dd>
          <span v-for="line in addressLines" :key="line" class="confirm__addressLine">{{ line }}</span>
        </dd>
      </template>
      <template v-for="row in summaryRows" :key="row.label">
        <dt>{{ row.label }}</dt>
        <dd>{{ row.value }}</dd>
      </template>
    </dl>

    <button class="confirm__toggle" type="button" @click="showRaw = !showRaw">
      {{ showRaw ? 'Verberg' : 'Bekijk' }} volledige gegevens
    </button>
    <pre v-if="showRaw" class="confirm__raw">{{ JSON.stringify(input, null, 2) }}</pre>

    <div v-if="state === 'pending'" class="confirm__actions">
      <button
        class="btn btn--primary"
        type="button"
        :disabled="busy"
        @click="emit('approve')"
      >
        Aanmaken
      </button>
      <button class="btn" type="button" :disabled="busy" @click="showReason = !showReason">
        Annuleren
      </button>
    </div>

    <div v-if="showReason && state === 'pending'" class="confirm__reason">
      <label for="decline-reason">Wat moet er anders? (optioneel)</label>
      <input
        id="decline-reason"
        v-model="reason"
        type="text"
        placeholder="Bijv. huisnummer moet 12b zijn"
        @keydown.enter.prevent="submitDecline"
      />
      <button class="btn" type="button" @click="submitDecline">Versturen</button>
    </div>

    <p v-if="state === 'approved'" class="confirm__resolved">Goedgekeurd en aangemaakt.</p>
    <p v-else-if="state === 'declined'" class="confirm__resolved">Geannuleerd.</p>
  </div>
</template>

<style scoped>
.confirm {
  border: 1px solid var(--warn-border);
  background: var(--warn-bg);
  border-radius: 12px;
  padding: 16px;
  margin: 12px 0;
}

.confirm--approved,
.confirm--declined {
  border-color: var(--border);
  background: var(--surface-muted);
}

.confirm__header {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 8px;
}

.confirm__header h3 {
  font-size: 15px;
  margin: 0;
}

.confirm__icon {
  width: 22px;
  height: 22px;
  flex-shrink: 0;
  border-radius: 50%;
  background: var(--warn-accent);
  color: var(--surface);
  font-weight: 700;
  font-size: 13px;
  display: grid;
  place-items: center;
}

.confirm__lead {
  margin: 0 0 12px;
  font-size: 13px;
  color: var(--text-muted);
}

.confirm__carrier {
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 0 0 10px;
  font-size: 14px;
  font-weight: 600;
}

.confirm__carrierLogo {
  width: 28px;
  height: 28px;
  object-fit: contain;
  border-radius: 6px;
  background: var(--surface);
  padding: 2px;
}

.confirm__body {
  display: grid;
  grid-template-columns: minmax(96px, auto) 1fr;
  gap: 6px 16px;
  margin: 0 0 12px;
  font-size: 14px;
}

.confirm__body dt {
  color: var(--text-muted);
}

.confirm__body dd {
  margin: 0;
}

.confirm__addressLine {
  display: block;
}

.confirm__toggle {
  background: none;
  border: none;
  padding: 0;
  font-size: 13px;
  color: var(--accent);
  cursor: pointer;
  text-decoration: underline;
}

.confirm__raw {
  margin: 8px 0 0;
  padding: 10px;
  background: var(--code-bg);
  border-radius: 8px;
  font-size: 12px;
  overflow-x: auto;
}

.confirm__actions {
  display: flex;
  gap: 8px;
  margin-top: 14px;
}

.confirm__reason {
  display: flex;
  gap: 8px;
  align-items: center;
  margin-top: 12px;
  flex-wrap: wrap;
}

.confirm__reason label {
  font-size: 13px;
  color: var(--text-muted);
  flex-basis: 100%;
}

.confirm__reason input {
  flex: 1 1 220px;
}

.confirm__resolved {
  margin: 12px 0 0;
  font-size: 13px;
  color: var(--text-muted);
}
</style>
